import { createFileRoute, Link } from "@tanstack/react-router";
import { ReadWriteOnly } from "@/components/common/read-write-only";
import { ArrowLeft, Upload } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDrive, useProgress } from "@/hooks/use-ballot-access";
import { useSession } from "@/hooks/use-session";
import { DriveDetail } from "@/components/ballot-access/DriveDetail";
import { ImportBatchesList } from "@/components/ballot-access/ImportBatchesList";
import { ImportSignersModal } from "@/components/ballot-access/ImportSignersModal";
import { OcrSheetStatusCard } from "@/components/ballot-access/OcrSheetStatusCard";
import { OcrSheetUploadButton } from "@/components/ballot-access/OcrSheetUploadButton";
import { SignerLogForm } from "@/components/ballot-access/SignerLogForm";
import { SignerTable } from "@/components/ballot-access/SignerTable";

export const Route = createFileRoute("/_app/_/ballot-access/$driveId")({
	component: DriveDetailPage,
});

function DriveDetailPage() {
	const { driveId } = Route.useParams();
	const session = useSession();
	const campaignId = session.campaign?.id ?? 0;
	const driveIdNum = Number(driveId);
	const [importOpen, setImportOpen] = useState(false);

	const { data: drive, isLoading: driveLoading, isError: driveError } = useDrive(
		campaignId,
		driveIdNum,
	);
	const { data: progress } = useProgress(campaignId);

	if (!driveLoading && driveError) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-3xl">
					<div className="flex items-center gap-3 mb-6">
						<Button variant="ghost" size="icon" asChild>
							<Link to="/ballot-access"><ArrowLeft className="h-4 w-4" /></Link>
						</Button>
						<h1 className="text-2xl font-semibold">Drive Not Found</h1>
					</div>
					<p className="text-muted-foreground">This petition drive no longer exists or has been deleted.</p>
				</div>
			</div>
		);
	}

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<p className="text-muted-foreground">Loading campaign...</p>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-3xl space-y-6">
				{/* Header */}
				<div className="flex items-center gap-3">
					<Button variant="ghost" size="icon" asChild>
						<Link to="/ballot-access">
							<ArrowLeft className="h-4 w-4" />
						</Link>
					</Button>
					<div>
						<h1 className="text-2xl font-semibold">
							{driveLoading
								? "Loading..."
								: (drive?.name ?? "Petition Drive")}
						</h1>
						<p className="text-sm text-muted-foreground">Drive #{driveId}</p>
					</div>
				</div>

				{/* Drive summary */}
				<Card>
					<CardHeader>
						<CardTitle>Drive Summary</CardTitle>
					</CardHeader>
					<CardContent>
						{driveLoading ? (
							<Skeleton className="h-24 w-full" />
						) : drive ? (
							<DriveDetail drive={drive} />
						) : null}
					</CardContent>
				</Card>

				{/* Bulk ingestion controls (F1 import + F3 OCR) */}
				<div className="flex flex-col sm:flex-row gap-2">
					<ReadWriteOnly message="Read-only — you can't import signers"><Button
						variant="outline"
						onClick={() => setImportOpen(true)}
						data-testid="import-signers-btn"
					>
						<Upload className="h-4 w-4 mr-2" />
						Import Signers from CSV
					</Button></ReadWriteOnly>
					<OcrSheetUploadButton
						campaignId={campaignId}
						driveId={driveIdNum}
					/>
				</div>

				<ImportSignersModal
					open={importOpen}
					onOpenChange={setImportOpen}
					campaignId={campaignId}
					driveId={driveIdNum}
				/>

				{/* F3 OCR sheet status (in-flight + ready-for-review) */}
				<OcrSheetStatusCard
					campaignId={campaignId}
					driveId={driveIdNum}
				/>

				{/* F1 Import history */}
				<Card>
					<CardHeader>
						<CardTitle>Import History</CardTitle>
					</CardHeader>
					<CardContent>
						<ImportBatchesList
							campaignId={campaignId}
							driveId={driveIdNum}
						/>
					</CardContent>
				</Card>

				{/* Log signature form */}
				<Card>
					<CardHeader>
						<CardTitle>Log Signature</CardTitle>
					</CardHeader>
					<CardContent>
						<SignerLogForm
							campaignId={campaignId}
							driveId={driveIdNum}
							daysUntilDeadline={progress?.days_until_deadline ?? null}
						/>
					</CardContent>
				</Card>

				{/* Signers table */}
				<Card>
					<CardHeader>
						<CardTitle>Signers</CardTitle>
					</CardHeader>
					<CardContent>
						{driveLoading ? (
							<Skeleton className="h-32 w-full" />
						) : drive ? (
							<SignerTable
								campaignId={campaignId}
								driveId={driveIdNum}
								countPending={drive.count_pending}
							/>
						) : null}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
